import { FormattedMessage, defineMessage } from 'react-intl';
import _defineProperty from '@babel/runtime/helpers/defineProperty';
import _objectSpread from '@babel/runtime/helpers/objectSpread2';
import _slicedToArray from '@babel/runtime/helpers/slicedToArray';
import _toConsumableArray from '@babel/runtime/helpers/toConsumableArray';
import classNames from 'classnames';
import PropTypes from 'prop-types';
import React, { useMemo, useState, useCallback, useRef, useEffect } from 'react';
import { PropTypes as PropTypes$1 } from '@micromag/core';
import { ScreenElement } from '@micromag/core/components';
import { useScreenSize, useViewerContext, useViewerWebView, usePlaybackContext, usePlaybackMediaRef, useScreenRenderContext } from '@micromag/core/contexts';
import { useTrackScreenEvent, useDimensionObserver } from '@micromag/core/hooks';
import { isTextFilled, getStyleFromColor, isHeaderFilled, isFooterFilled, getFooterProps } from '@micromag/core/utils';
import Background from '@micromag/element-background';
import Container from '@micromag/element-container';
import Footer from '@micromag/element-footer';
import Header from '@micromag/element-header';
import Heading from '@micromag/element-heading';
import Layout from '@micromag/element-layout';
import Scroll from '@micromag/element-scroll';
import Text from '@micromag/element-text';
import Visual from '@micromag/element-visual';
import _objectDestructuringEmpty from '@babel/runtime/helpers/objectDestructuringEmpty';
import _extends from '@babel/runtime/helpers/extends';

var styles = {"container":"micromag-screen-timeline-container","background":"micromag-screen-timeline-background","empty":"micromag-screen-timeline-empty","content":"micromag-screen-timeline-content","emptyTitle":"micromag-screen-timeline-emptyTitle","item":"micromag-screen-timeline-item","element":"micromag-screen-timeline-element","hidden":"micromag-screen-timeline-hidden","body":"micromag-screen-timeline-body","timeline":"micromag-screen-timeline-timeline","line":"micromag-screen-timeline-line","bullet":"micromag-screen-timeline-bullet","last":"micromag-screen-timeline-last","title":"micromag-screen-timeline-title","description":"micromag-screen-timeline-description","imageContainer":"micromag-screen-timeline-imageContainer","image":"micromag-screen-timeline-image","video":"micromag-screen-timeline-video","callToAction":"micromag-screen-timeline-callToAction","disabled":"micromag-screen-timeline-disabled","isPlaceholder":"micromag-screen-timeline-isPlaceholder","scroll":"micromag-screen-timeline-scroll","circleBulletShape":"micromag-screen-timeline-circleBulletShape","withoutLines":"micromag-screen-timeline-withoutLines"};

var propTypes = {
  layout: PropTypes.oneOf(['normal', 'title-description-image', 'title-image-description', 'image-title-description']),
  title: PropTypes$1.headingElement,
  items: PropTypes.arrayOf(PropTypes$1.textElement),
  itemTitleStyle: PropTypes$1.textStyle,
  itemDescriptionStyle: PropTypes$1.textStyle,
  withoutLine: PropTypes.bool,
  bulletColor: PropTypes$1.color,
  lineColor: PropTypes$1.color,
  bulletShape: PropTypes.oneOf(['circle', 'square']),
  bulletFilled: PropTypes.bool,
  illustrated: PropTypes.bool,
  spacing: PropTypes.number,
  header: PropTypes$1.header,
  footer: PropTypes$1.footer,
  background: PropTypes$1.backgroundElement,
  current: PropTypes.bool,
  active: PropTypes.bool,
  preload: PropTypes.bool,
  type: PropTypes.string,
  className: PropTypes.string
};
var defaultProps = {
  layout: 'normal',
  title: null,
  items: [null],
  itemTitleStyle: null,
  itemDescriptionStyle: null,
  withoutLine: false,
  bulletColor: null,
  lineColor: null,
  bulletShape: 'circle',
  bulletFilled: true,
  illustrated: false,
  spacing: 20,
  header: null,
  footer: null,
  background: null,
  current: true,
  active: true,
  preload: true,
  type: null,
  className: null
};
var Timeline = function Timeline(_ref) {
  var layout = _ref.layout,
    title = _ref.title,
    items = _ref.items,
    itemTitleStyle = _ref.itemTitleStyle,
    itemDescriptionStyle = _ref.itemDescriptionStyle,
    withoutLine = _ref.withoutLine,
    bulletColor = _ref.bulletColor,
    lineColor = _ref.lineColor,
    bulletShape = _ref.bulletShape,
    bulletFilled = _ref.bulletFilled,
    illustrated = _ref.illustrated,
    spacing = _ref.spacing,
    header = _ref.header,
    footer = _ref.footer,
    background = _ref.background,
    current = _ref.current,
    active = _ref.active,
    preload = _ref.preload,
    type = _ref.type,
    className = _ref.className;
  var trackScreenEvent = useTrackScreenEvent(type);
  var _useScreenSize = useScreenSize(),
    width = _useScreenSize.width,
    height = _useScreenSize.height,
    imageResolution = _useScreenSize.imageResolution,
    resolution = _useScreenSize.resolution;
  var _useViewerContext = useViewerContext(),
    viewerTopHeight = _useViewerContext.topHeight,
    viewerBottomHeight = _useViewerContext.bottomHeight,
    viewerBottomSidesWidth = _useViewerContext.bottomSidesWidth;
  var _useViewerWebView = useViewerWebView(),
    openWebView = _useViewerWebView.open;
  var _usePlaybackContext = usePlaybackContext(),
    muted = _usePlaybackContext.muted;
  var mediaRef = usePlaybackMediaRef(current);
  var _useScreenRenderConte = useScreenRenderContext(),
    isView = _useScreenRenderConte.isView,
    isPreview = _useScreenRenderConte.isPreview,
    isPlaceholder = _useScreenRenderConte.isPlaceholder,
    isEdit = _useScreenRenderConte.isEdit,
    isStatic = _useScreenRenderConte.isStatic,
    isCapture = _useScreenRenderConte.isCapture;
  var finalItems = useMemo(function () {
    return isPlaceholder ? _toConsumableArray(new Array(5)).map(function () {
      return {};
    }) : items || [null];
  }, [isPlaceholder, items]);
  var hasTitle = isTextFilled(title);
  var itemsCount = finalItems !== null ? finalItems.length : 0;
  var hasItems = finalItems !== null && itemsCount;
  var imagesCount = hasItems ? finalItems.reduce(function (acc, curr) {
    var _ref2 = curr || {},
      _ref2$image = _ref2.image,
      image = _ref2$image === void 0 ? null : _ref2$image;
    return acc + (image !== null ? 1 : 0);
  }, 0) : 0;
  var _useState = useState(0),
    _useState2 = _slicedToArray(_useState, 2),
    imagesLoaded = _useState2[0],
    setImagesLoaded = _useState2[1];
  var ready = imagesLoaded >= imagesCount;
  // const transitionsPlaying = current && ready;
  var transitionDisabled = isStatic || isCapture || isPlaceholder || isPreview || isEdit;
  var scrollingDisabled = !isEdit && transitionDisabled || !current;
  var backgroundPlaying = current && (isView || isEdit);
  var mediaShouldLoad = current || preload;
  var onImageLoaded = useCallback(function () {
    setImagesLoaded(function (count) {
      return count + 1;
    });
  }, [setImagesLoaded]);
  var firstLineRef = useRef(null);
  var firstContentRef = useRef(null);
  var _useState3 = useState(0),
    _useState4 = _slicedToArray(_useState3, 2),
    imageWidth = _useState4[0],
    setImageWidth = _useState4[1];
  useEffect(function () {
    if (firstContentRef.current === null) {
      return;
    }
    if (firstLineRef.current !== null) {
      setImageWidth(firstContentRef.current.offsetWidth - firstLineRef.current.offsetWidth);
    } else {
      setImageWidth(firstContentRef.current.offsetWidth);
    }
  }, [width, height, finalItems]);
  var timelineElements = (finalItems || []).map(function (item, itemI) {
    var _ref3 = item || {},
      _ref3$title = _ref3.title,
      itemTitle = _ref3$title === void 0 ? null : _ref3$title,
      _ref3$description = _ref3.description,
      description = _ref3$description === void 0 ? null : _ref3$description,
      _ref3$image = _ref3.image,
      image = _ref3$image === void 0 ? null : _ref3$image;
    var hasItemTitle = isTextFilled(itemTitle);
    var hasDescription = isTextFilled(description);
    var hasImage = image !== null;
    var elementsTypes = (layout === 'normal' ? 'title-description-image' : layout).split('-');
    var titleIndex = elementsTypes.indexOf('title');
    var imageIndex = elementsTypes.indexOf('image');
    if (!illustrated) {
      elementsTypes.splice(imageIndex, 1);
    }
    var typesCount = elementsTypes.length;
    var _ref4 = itemTitle || {},
      titleTextStyle = _ref4.textStyle;
    var _ref5 = description || {},
      descriptionTextStyle = _ref5.textStyle;
    return /*#__PURE__*/React.createElement("div", {
      className: styles.item,
      key: "item-".concat(itemI)
    }, elementsTypes.map(function (elementType, typeI) {
      var elementContent = null;
      var hasElement = false;
      switch (elementType) {
        case 'title':
          hasElement = hasItemTitle;
          elementContent = /*#__PURE__*/React.createElement("div", {
            className: styles.title
          }, /*#__PURE__*/React.createElement(ScreenElement, {
            placeholder: "title",
            emptyLabel: /*#__PURE__*/React.createElement(FormattedMessage, {
              id: "bmO4Ss",
              defaultMessage: [{
                "type": 0,
                "value": "Entry Title"
              }]
            }),
            emptyClassName: styles.empty,
            isEmpty: !hasItemTitle
          }, hasItemTitle ? /*#__PURE__*/React.createElement(Heading, Object.assign({}, itemTitle, {
            textStyle: _objectSpread(_objectSpread({}, itemTitleStyle || null), titleTextStyle)
          })) : null));
          break;
        case 'image':
          hasElement = hasImage;
          elementContent = /*#__PURE__*/React.createElement("div", {
            className: styles.imageContainer
          }, /*#__PURE__*/React.createElement(ScreenElement, {
            placeholder: "image",
            emptyLabel: /*#__PURE__*/React.createElement(FormattedMessage, {
              id: "ebsPd4",
              defaultMessage: [{
                "type": 0,
                "value": "Image"
              }]
            }),
            emptyClassName: styles.empty,
            isEmpty: !hasImage
          }, hasImage ? /*#__PURE__*/React.createElement(Visual, {
            className: styles.image,
            videoClassName: styles.video,
            media: image,
            width: imageWidth,
            resolution: resolution,
            playing: backgroundPlaying,
            active: active,
            shouldLoad: mediaShouldLoad,
            withoutVideo: isPreview,
            onLoaded: onImageLoaded
          }) : null));
          break;
        case 'description':
          hasElement = hasDescription;
          elementContent = /*#__PURE__*/React.createElement("div", {
            className: styles.description
          }, /*#__PURE__*/React.createElement(ScreenElement, {
            placeholder: "text",
            emptyLabel: /*#__PURE__*/React.createElement(FormattedMessage, {
              id: "507VAi",
              defaultMessage: [{
                "type": 0,
                "value": "Description"
              }]
            }),
            emptyClassName: styles.empty,
            isEmpty: !hasDescription
          }, hasDescription ? /*#__PURE__*/React.createElement(Text, Object.assign({}, description, {
            textStyle: _objectSpread(_objectSpread({}, itemDescriptionStyle || null), descriptionTextStyle)
          })) : null));
          break;
        default:
          elementContent = null;
          hasElement = false;
      }
      var firstItem = itemI === 0;
      var lastItem = itemI === itemsCount - 1;
      var lastType = typeI === typesCount - 1;
      var topLineHidden = firstItem && typeI <= titleIndex || lastItem && typeI > titleIndex;
      var bottomLineHidden = firstItem && typeI < titleIndex || lastItem && typeI >= titleIndex;
      return /*#__PURE__*/React.createElement("div", {
        key: "element-".concat(elementType),
        className: classNames([styles.element, styles["element-".concat(elementType)], _defineProperty({}, styles.hidden, (isView || isStatic) && !hasElement)]),
        ref: itemI === 0 ? firstContentRef : null
      }, !withoutLine ? /*#__PURE__*/React.createElement("div", {
        className: styles.timeline,
        ref: itemI === 0 ? firstLineRef : null
      }, /*#__PURE__*/React.createElement("div", {
        className: classNames([styles.line, _defineProperty({}, styles.hidden, topLineHidden)]),
        style: _objectSpread({}, !topLineHidden ? getStyleFromColor(lineColor, 'backgroundColor') : null)
      }), elementType === 'title' ? /*#__PURE__*/React.createElement("div", {
        className: styles.bullet,
        style: _objectSpread(_objectSpread({}, getStyleFromColor(bulletColor, 'borderColor')), bulletFilled ? getStyleFromColor(bulletColor, 'backgroundColor') : null)
      }) : null, /*#__PURE__*/React.createElement("div", {
        className: classNames([styles.line, _defineProperty({}, styles.hidden, bottomLineHidden)]),
        style: _objectSpread({}, !bottomLineHidden ? getStyleFromColor(lineColor, 'backgroundColor') : null)
      })) : null, /*#__PURE__*/React.createElement("div", {
        className: classNames([styles.body, _defineProperty({}, styles.last, lastType && !lastItem)])
      }, elementContent));
    }));
  });

  // Call to Action
  var hasHeader = isHeaderFilled(header);
  var hasFooter = isFooterFilled(footer);
  var footerProps = getFooterProps(footer, {
    isView: isView,
    current: current,
    openWebView: openWebView,
    isPreview: isPreview
  });
  var _useState5 = useState(false),
    _useState6 = _slicedToArray(_useState5, 2),
    scrolledBottom = _useState6[0],
    setScrolledBottom = _useState6[1];
  var _useDimensionObserver = useDimensionObserver(),
    footerRef = _useDimensionObserver.ref,
    _useDimensionObserver2 = _useDimensionObserver.height,
    footerHeight = _useDimensionObserver2 === void 0 ? 0 : _useDimensionObserver2;
  var onScrolledBottom = useCallback(function (_ref0) {
    var initial = _ref0.initial;
    if (initial) {
      trackScreenEvent('scroll', 'Screen');
    }
    setScrolledBottom(true);
  }, [trackScreenEvent]);
  var onScrolledNotBottom = useCallback(function () {
    setScrolledBottom(false);
  }, [setScrolledBottom]);
  return /*#__PURE__*/React.createElement("div", {
    className: classNames([styles.container, _defineProperty(_defineProperty(_defineProperty(_defineProperty({}, className, className !== null), styles.isPlaceholder, isPlaceholder), styles["".concat(bulletShape, "BulletShape")], bulletShape !== null), styles.withoutLines, itemsCount < 2)]),
    "data-screen-ready": ready
  }, /*#__PURE__*/React.createElement(Container, {
    width: width,
    height: height,
    className: styles.content
  }, /*#__PURE__*/React.createElement(Scroll, {
    className: styles.scroll,
    verticalAlign: "middle",
    disabled: scrollingDisabled,
    onScrolledBottom: onScrolledBottom,
    onScrolledNotBottom: onScrolledNotBottom,
    withShadow: true
  }, /*#__PURE__*/React.createElement(Layout, {
    style: !isPlaceholder ? {
      padding: spacing,
      paddingTop: (!isPreview ? viewerTopHeight : 0) + (hasHeader ? spacing / 2 : spacing),
      paddingBottom: (current && !isPreview ? viewerBottomHeight : 0) + (footerHeight || spacing)
    } : null
  }, !isPlaceholder && hasHeader ? /*#__PURE__*/React.createElement("div", {
    style: {
      paddingBottom: spacing
    }
  }, /*#__PURE__*/React.createElement(Header, header)) : null, /*#__PURE__*/React.createElement(ScreenElement, {
    placeholder: "Title",
    emptyLabel: /*#__PURE__*/React.createElement(FormattedMessage, {
      id: "BSTWf8",
      defaultMessage: [{
        "type": 0,
        "value": "Title"
      }]
    }),
    emptyClassName: classNames([styles.empty, styles.emptyTitle]),
    isEmpty: !hasTitle
  }, hasTitle ? /*#__PURE__*/React.createElement(Heading, Object.assign({
    className: styles.title
  }, title)) : null), timelineElements)), !isPlaceholder && hasFooter ? /*#__PURE__*/React.createElement("div", {
    ref: footerRef,
    className: classNames([styles.callToAction, _defineProperty({}, styles.disabled, !scrolledBottom)]),
    style: {
      transform: current && !isPreview ? "translate(0, -".concat(viewerBottomHeight, "px)") : null,
      paddingLeft: Math.max(spacing / 2, viewerBottomSidesWidth),
      paddingRight: Math.max(spacing / 2, viewerBottomSidesWidth),
      paddingTop: spacing / 2,
      paddingBottom: spacing / 2
    }
  }, /*#__PURE__*/React.createElement(Footer, footerProps)) : null), !isPlaceholder ? /*#__PURE__*/React.createElement(Background, {
    background: background,
    width: width,
    height: height,
    resolution: imageResolution,
    playing: backgroundPlaying,
    muted: muted,
    shouldLoad: mediaShouldLoad,
    mediaRef: mediaRef,
    withoutVideo: isPreview,
    className: styles.background
  }) : null);
};
Timeline.propTypes = propTypes;
Timeline.defaultProps = defaultProps;

var TimelineIllustratedScreen = function TimelineIllustratedScreen(_ref) {
  var props = _extends({}, (_objectDestructuringEmpty(_ref), _ref));
  return /*#__PURE__*/React.createElement(Timeline, Object.assign({}, props, {
    illustrated: true
  }));
};

var definition = [{
  id: 'timeline',
  type: 'screen',
  group: {
    label: defineMessage({
      id: "cGItoy",
      defaultMessage: [{
        "type": 0,
        "value": "List"
      }]
    }),
    order: 8
  },
  title: defineMessage({
    id: "GzoKNb",
    defaultMessage: [{
      "type": 0,
      "value": "Timeline"
    }]
  }),
  component: Timeline,
  layouts: ['normal'],
  // transforms,
  fields: [{
    name: 'title',
    type: 'heading-element',
    // inline: true,
    theme: {
      textStyle: 'heading1'
    },
    label: defineMessage({
      id: "N25iDO",
      defaultMessage: [{
        "type": 0,
        "value": "Title"
      }]
    })
  }, {
    name: 'items',
    type: 'entries',
    theme: {
      title: {
        textStyle: 'heading2'
      },
      description: {
        textStyle: 'text'
      }
    },
    label: defineMessage({
      id: "3ifj0j",
      defaultMessage: [{
        "type": 0,
        "value": "Entries"
      }]
    })
  }, {
    type: 'fields',
    label: defineMessage({
      id: "xac4z/",
      defaultMessage: [{
        "type": 0,
        "value": "Entry styles"
      }]
    }),
    isList: true,
    fields: [{
      name: 'itemTitleStyle',
      type: 'text-style-form',
      label: defineMessage({
        id: "+AEVbJ",
        defaultMessage: [{
          "type": 0,
          "value": "Title"
        }]
      })
    }, {
      name: 'itemDescriptionStyle',
      type: 'text-style-form',
      label: defineMessage({
        id: "ZCe0r4",
        defaultMessage: [{
          "type": 0,
          "value": "Description"
        }]
      })
    }]
  }, {
    name: 'bulletShape',
    type: 'radios',
    defaultValue: 'circle',
    options: [{
      value: 'circle',
      label: defineMessage({
        id: "+uInZz",
        defaultMessage: [{
          "type": 0,
          "value": "Circle"
        }]
      })
    }, {
      value: 'square',
      label: defineMessage({
        id: "fusnxf",
        defaultMessage: [{
          "type": 0,
          "value": "Square"
        }]
      })
    }],
    label: defineMessage({
      id: "twTaAZ",
      defaultMessage: [{
        "type": 0,
        "value": "Bullet shape"
      }]
    })
  }, {
    name: 'withoutLine',
    type: 'toggle',
    defaultValue: false,
    label: defineMessage({
      id: "N4lZhy",
      defaultMessage: [{
        "type": 0,
        "value": "Without line"
      }]
    })
  }, {
    name: 'bulletFilled',
    type: 'toggle',
    defaultValue: false,
    label: defineMessage({
      id: "YWcp4v",
      defaultMessage: [{
        "type": 0,
        "value": "Bullet filled"
      }]
    })
  }, {
    name: 'bulletColor',
    type: 'color',
    defaultValue: {
      alpha: 1,
      color: '#FFFFFF'
    },
    label: defineMessage({
      id: "uNpHnA",
      defaultMessage: [{
        "type": 0,
        "value": "Bullet color"
      }]
    })
  }, {
    name: 'lineColor',
    type: 'color',
    defaultValue: {
      alpha: 1,
      color: '#FFFFFF'
    },
    label: defineMessage({
      id: "RHMS2f",
      defaultMessage: [{
        "type": 0,
        "value": "Line color"
      }]
    })
  }, {
    name: 'background',
    type: 'background',
    label: defineMessage({
      id: "+MPZRu",
      defaultMessage: [{
        "type": 0,
        "value": "Background"
      }]
    })
  }, {
    name: 'header',
    type: 'header',
    label: defineMessage({
      id: "rhuDxI",
      defaultMessage: [{
        "type": 0,
        "value": "Header"
      }]
    }),
    theme: {
      badge: {
        label: {
          textStyle: 'badge'
        },
        boxStyle: 'badge'
      }
    }
  }, {
    name: 'footer',
    type: 'footer',
    label: defineMessage({
      id: "g4nybp",
      defaultMessage: [{
        "type": 0,
        "value": "Footer"
      }]
    }),
    theme: {
      callToAction: {
        label: {
          textStyle: 'cta'
        },
        boxStyle: 'cta'
      }
    }
  }]
}, {
  id: 'timeline-illustrated',
  type: 'screen',
  group: {
    label: defineMessage({
      id: "cGItoy",
      defaultMessage: [{
        "type": 0,
        "value": "List"
      }]
    }),
    order: 8
  },
  title: defineMessage({
    id: "33UbSd",
    defaultMessage: [{
      "type": 0,
      "value": "Timeline with images"
    }]
  }),
  component: TimelineIllustratedScreen,
  layouts: ['title-image-description', 'title-description-image', 'image-title-description'],
  fields: [{
    name: 'title',
    type: 'heading-element',
    // inline: true,
    theme: {
      textStyle: 'heading1'
    },
    label: defineMessage({
      id: "N25iDO",
      defaultMessage: [{
        "type": 0,
        "value": "Title"
      }]
    })
  }, {
    name: 'layout',
    type: 'screen-layout',
    defaultValue: 'title-image-description',
    label: defineMessage({
      id: "4iBXj2",
      defaultMessage: [{
        "type": 0,
        "value": "Layout"
      }]
    })
  }, {
    name: 'items',
    type: 'entries-with-visual',
    theme: {
      title: {
        textStyle: 'heading2'
      },
      description: {
        textStyle: 'text'
      }
    },
    label: defineMessage({
      id: "3ifj0j",
      defaultMessage: [{
        "type": 0,
        "value": "Entries"
      }]
    })
  }, {
    type: 'fields',
    label: defineMessage({
      id: "xac4z/",
      defaultMessage: [{
        "type": 0,
        "value": "Entry styles"
      }]
    }),
    isList: true,
    fields: [{
      name: 'itemTitleStyle',
      type: 'text-style-form',
      label: defineMessage({
        id: "+AEVbJ",
        defaultMessage: [{
          "type": 0,
          "value": "Title"
        }]
      })
    }, {
      name: 'itemDescriptionStyle',
      type: 'text-style-form',
      label: defineMessage({
        id: "ZCe0r4",
        defaultMessage: [{
          "type": 0,
          "value": "Description"
        }]
      })
    }]
  }, {
    name: 'withoutLine',
    type: 'toggle',
    defaultValue: false,
    label: defineMessage({
      id: "N4lZhy",
      defaultMessage: [{
        "type": 0,
        "value": "Without line"
      }]
    })
  }, {
    name: 'bulletShape',
    type: 'radios',
    defaultValue: 'circle',
    options: [{
      value: 'circle',
      label: defineMessage({
        id: "+uInZz",
        defaultMessage: [{
          "type": 0,
          "value": "Circle"
        }]
      })
    }, {
      value: 'square',
      label: defineMessage({
        id: "fusnxf",
        defaultMessage: [{
          "type": 0,
          "value": "Square"
        }]
      })
    }],
    label: defineMessage({
      id: "twTaAZ",
      defaultMessage: [{
        "type": 0,
        "value": "Bullet shape"
      }]
    })
  }, {
    name: 'bulletFilled',
    type: 'toggle',
    defaultValue: false,
    label: defineMessage({
      id: "YWcp4v",
      defaultMessage: [{
        "type": 0,
        "value": "Bullet filled"
      }]
    })
  }, {
    name: 'bulletColor',
    type: 'color',
    defaultValue: {
      alpha: 1,
      color: '#FFFFFF'
    },
    label: defineMessage({
      id: "uNpHnA",
      defaultMessage: [{
        "type": 0,
        "value": "Bullet color"
      }]
    })
  }, {
    name: 'lineColor',
    type: 'color',
    defaultValue: {
      alpha: 1,
      color: '#FFFFFF'
    },
    label: defineMessage({
      id: "RHMS2f",
      defaultMessage: [{
        "type": 0,
        "value": "Line color"
      }]
    })
  }, {
    name: 'background',
    type: 'background',
    label: defineMessage({
      id: "+MPZRu",
      defaultMessage: [{
        "type": 0,
        "value": "Background"
      }]
    })
  }, {
    name: 'header',
    type: 'header',
    label: defineMessage({
      id: "rhuDxI",
      defaultMessage: [{
        "type": 0,
        "value": "Header"
      }]
    }),
    theme: {
      badge: {
        label: {
          textStyle: 'badge'
        },
        boxStyle: 'badge'
      }
    }
  }, {
    name: 'footer',
    type: 'footer',
    label: defineMessage({
      id: "g4nybp",
      defaultMessage: [{
        "type": 0,
        "value": "Footer"
      }]
    }),
    theme: {
      callToAction: {
        label: {
          textStyle: 'cta'
        },
        boxStyle: 'cta'
      }
    }
  }]
}];

export { TimelineIllustratedScreen, Timeline as TimelineScreen, definition as default };
